import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const PAGE_SIZE = 50;

const ENTITY_OPTIONS = [
  { value: "", label: "All Sections" },
  { value: "AcademyPlayer", label: "Players" },
  { value: "AcademyBatch", label: "Batches" },
  { value: "AcademyCoach", label: "Coaches" },
  { value: "AcademyAttendance", label: "Attendance" },
  { value: "AcademyCoachAttendance", label: "Coach Attendance" },
  { value: "AcademyFeeRecord", label: "Fees" },
  { value: "MonthlyFeePayment", label: "Monthly Payments" },
  { value: "AcademyAdmissionItem", label: "Enrollment / Admission" },
  { value: "AcademyInjury", label: "Injuries" },
  { value: "AcademyTrainingLog", label: "Training Logs" },
  { value: "AcademyExpense", label: "Expenses" },
  { value: "AcademyFeePlan", label: "Fee Plans" },
  { value: "AcademyConfigSettings", label: "Config Settings" },
  { value: "AcademyConfig", label: "Academy Config (Bulk)" },
];

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "Created", label: "Created" },
  { value: "Updated", label: "Updated" },
  { value: "Deleted", label: "Deleted" },
];

const ACTION_COLOR = { Created: "success", Updated: "info", Deleted: "error" };

function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const TODAY = localDateStr(new Date());
const WEEK_AGO = localDateStr(new Date(Date.now() - 7 * 86400000));

const ENTITY_LABELS = {
  AcademyPlayer: "Player",
  AcademyBatch: "Batch",
  AcademyCoach: "Coach",
  AcademyAttendance: "Attendance",
  AcademyCoachAttendance: "Coach Attendance",
  AcademyFeeRecord: "Fee",
  MonthlyFeePayment: "Monthly Payment",
  AcademyAdmissionItem: "Enrollment",
  AcademyInjury: "Injury",
  AcademyTrainingLog: "Training Log",
  AcademyExpense: "Expense",
  AcademyFeePlan: "Fee Plan",
  AcademyConfigSettings: "Config",
  AcademyConfig: "Academy Config",
};

// ── PDF download ─────────────────────────────────────────────────────────────────
async function downloadAuditPDF(logs, filters) {
  const { buildBrandedReport } = await import("config/branding");

  const { doc, autoTable, startY, headStyles, altStyles } = await buildBrandedReport(
    "Edit History Report",
    [
      `Section: ${filters.entityLabel || "All"}  |  Action: ${filters.action || "All"}  |  User: ${filters.user || "All"}`,
      `Period: ${filters.from || "—"} to ${filters.to || "—"}  |  Records: ${logs.length}`,
    ],
  );

  autoTable(doc, {
    startY,
    head: [["Timestamp", "Section", "Record", "Action", "Field", "Old Value", "New Value", "Changed By"]],
    body: logs.map((log) => [
      new Date(log.changedAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }),
      ENTITY_LABELS[log.entityName] ?? log.entityName,
      log.recordName ?? "—",
      log.action,
      log.fieldName ?? "—",
      log.oldValue ?? "—",
      log.newValue ?? "—",
      log.changedBy ?? "System",
    ]),
    styles:     { fontSize: 7, cellPadding: 2, overflow: "linebreak" },
    headStyles: { ...headStyles, fontSize: 7 },
    alternateRowStyles: altStyles,
    columnStyles: {
      0: { cellWidth: 34 },
      1: { cellWidth: 22 },
      2: { cellWidth: 35 },
      3: { cellWidth: 18 },
      4: { cellWidth: 30 },
      5: { cellWidth: 50, textColor: [180, 50, 50] },
      6: { cellWidth: 50, textColor: [30, 120, 50] },
      7: { cellWidth: 28 },
    },
  });

  const { addWatermarkToPdf } = await import("config/branding");
  await addWatermarkToPdf(doc);
  doc.save(`edit-history-${new Date().toISOString().slice(0, 10)}.pdf`);
}

function AuditRow({ log }) {
  return (
    <SoftBox display="flex" alignItems="flex-start" py={1.5} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", gap: 2, flexWrap: "wrap" }}>
      <SoftBox flex="0 0 130px">
        <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {new Date(log.changedAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 90px">
        <SoftTypography variant="caption" fontWeight="medium" color="dark">
          {ENTITY_LABELS[log.entityName] ?? log.entityName}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 150px" sx={{ minWidth: 0 }}>
        <SoftTypography variant="caption" color="secondary"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {log.recordName ?? "—"}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
        <SoftBadge badgeContent={log.action} color={ACTION_COLOR[log.action] ?? "info"} size="xs" container />
        {log.isCritical && (
          <SoftBadge badgeContent="CRITICAL" color="error" size="xs" container
            sx={{ "& .MuiBadge-badge": { fontWeight: 900 } }} />
        )}
      </SoftBox>
      <SoftBox flex="1" sx={{ minWidth: 0 }}>
        {log.fieldName ? (
          <SoftBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Chip label={log.fieldName} size="small" sx={{ fontSize: "0.68rem", height: 20 }} />
            {log.oldValue && (
              <SoftTypography variant="caption" color="error" sx={{ textDecoration: "line-through" }}>
                {log.oldValue}
              </SoftTypography>
            )}
            {log.oldValue && log.newValue && (
              <Icon sx={{ fontSize: "0.9rem", color: "text.secondary" }}>arrow_forward</Icon>
            )}
            {log.newValue && (
              <SoftTypography variant="caption" color="success">{log.newValue}</SoftTypography>
            )}
          </SoftBox>
        ) : (
          <SoftBox display="flex" flexDirection="column" gap={0.25}>
            <SoftTypography variant="caption" color="text">
              {log.action === "Created" ? `New ${ENTITY_LABELS[log.entityName] ?? log.entityName} created`
                : log.action === "Deleted" ? `${ENTITY_LABELS[log.entityName] ?? log.entityName} deleted`
                : log.action}
            </SoftTypography>
            {log.entityId && log.entityId !== "00000000-0000-0000-0000-000000000000" && (
              <SoftTypography variant="caption" color="secondary"
                sx={{ fontFamily: "monospace", fontSize: "0.62rem", opacity: 0.75, userSelect: "all" }}>
                ID: {log.entityId}
              </SoftTypography>
            )}
          </SoftBox>
        )}
      </SoftBox>
      <SoftBox flex="0 0 160px" textAlign="right" sx={{ minWidth: 0 }}>
        <SoftTypography variant="caption" color="text"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {log.changedBy ?? "System"}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}
AuditRow.propTypes = {
  log: PropTypes.object.isRequired,
};

function AuditLogs() {
  const [logs, setLogs]               = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [hasLoaded, setHasLoaded]     = useState(false);
  const [entityName, setEntityName]   = useState("");
  const [action, setAction]           = useState("");
  const [userFilter, setUserFilter]   = useState("");
  const [from, setFrom]               = useState(WEEK_AGO);
  const [to, setTo]                   = useState(TODAY);
  const [page, setPage]               = useState(1);
  const [downloading, setDownloading] = useState(false);

  const buildParams = useCallback((pg) => ({
    entityName:  entityName || undefined,
    action:      action || undefined,
    changedBy:   userFilter || undefined,
    from:        from || undefined,
    to:          to || undefined,
    page:        pg,
    pageSize:    PAGE_SIZE,
  }), [entityName, action, userFilter, from, to]);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const data = await academyService.getAuditLogs(buildParams(pg));
      const items = Array.isArray(data?.items) ? data.items : [];
      setLogs(items);
      setTotalCount(data?.totalCount ?? items.length);
      setPage(pg);
      setHasLoaded(true);
    } catch (err) {
      console.error(err);
      setLogs([]);
      setTotalCount(0);
    } finally { setLoading(false); }
  }, [buildParams]);

  const handleSearch = () => load(1);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // pageSize=0 → backend returns all matching records
      const data = await academyService.getAuditLogs({
        entityName:  entityName || undefined,
        action:      action || undefined,
        changedBy:   userFilter || undefined,
        from:        from || undefined,
        to:          to || undefined,
        page:        1,
        pageSize:    0,
      });
      const allLogs = Array.isArray(data?.items) ? data.items : [];
      if (allLogs.length === 0) return;
      const entityLabel = ENTITY_OPTIONS.find((o) => o.value === entityName)?.label ?? (entityName || "All");
      await downloadAuditPDF(allLogs, { entityLabel, action, user: userFilter, from, to });
    } finally { setDownloading(false); }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Edit History</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Field-level change tracking across all academy modules
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        <Card>
          {/* Filters */}
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
            <TextField select size="small" label="Section" value={entityName}
              onChange={(e) => setEntityName(e.target.value)} sx={{ minWidth: 175 }}>
              {ENTITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Action" value={action}
              onChange={(e) => setAction(e.target.value)} sx={{ minWidth: 130 }}>
              {ACTION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Changed By" value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Search user…" sx={{ minWidth: 150 }} />
            <TextField size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <TextField size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <SoftButton variant="gradient" color="info" size="small" onClick={handleSearch}>
              <Icon sx={{ mr: 0.5 }}>search</Icon> Search
            </SoftButton>
            {hasLoaded && (
              <SoftButton variant="outlined" color="info" size="small" onClick={handleDownload} disabled={downloading || totalCount === 0}>
                <Icon sx={{ mr: 0.5 }}>download</Icon> {downloading ? "Generating…" : `Download All (${totalCount})`}
              </SoftButton>
            )}
            {hasLoaded && (
              <SoftTypography variant="caption" color="text" ml="auto">
                {totalCount} record{totalCount !== 1 ? "s" : ""}
              </SoftTypography>
            )}
          </SoftBox>

          {/* Column headers */}
          <SoftBox display="flex" alignItems="center" px={2} py={1}
            sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", flexWrap: "wrap", gap: 2 }}>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px" }}>TIMESTAMP</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px" }}>SECTION</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 150px" }}>RECORD</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px" }}>ACTION</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>CHANGE</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 160px", textAlign: "right" }}>CHANGED BY</SoftTypography>
          </SoftBox>

          {loading ? (
            <FootballLoader label="Loading history…" />
          ) : !hasLoaded ? (
            <SoftBox p={4} textAlign="center">
              <Icon sx={{ fontSize: "2rem", color: "text.secondary" }}>history</Icon>
              <SoftTypography variant="caption" color="text" display="block" mt={1}>
                Set filters and press Search to view edit history
              </SoftTypography>
            </SoftBox>
          ) : logs.length === 0 ? (
            <SoftBox p={4} textAlign="center">
              <SoftTypography variant="caption" color="text">No history records found for the selected criteria</SoftTypography>
            </SoftBox>
          ) : (
            <>
              {logs.map((log) => <AuditRow key={log.id} log={log} />)}

              {/* Pagination */}
              {totalPages > 1 && (
                <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <SoftTypography variant="caption" color="text">
                    Page {page} of {totalPages} &nbsp;·&nbsp; showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
                  </SoftTypography>
                  <SoftBox display="flex" gap={1}>
                    <SoftButton variant="outlined" color="info" size="small"
                      disabled={page <= 1} onClick={() => load(page - 1)}>
                      <Icon>chevron_left</Icon> Prev
                    </SoftButton>
                    {/* Show up to 5 page buttons around current page */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let p;
                      if (totalPages <= 5) {
                        p = i + 1;
                      } else if (page <= 3) {
                        p = i + 1;
                      } else if (page >= totalPages - 2) {
                        p = totalPages - 4 + i;
                      } else {
                        p = page - 2 + i;
                      }
                      return (
                        <SoftButton key={p} size="small"
                          variant={p === page ? "gradient" : "outlined"}
                          color={p === page ? "info" : "secondary"}
                          onClick={() => load(p)}
                          sx={{ minWidth: 36, px: 1 }}>
                          {p}
                        </SoftButton>
                      );
                    })}
                    <SoftButton variant="outlined" color="info" size="small"
                      disabled={page >= totalPages} onClick={() => load(page + 1)}>
                      Next <Icon>chevron_right</Icon>
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              )}
            </>
          )}
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />
    </DashboardLayout>
  );
}

export default AuditLogs;
